import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import TextField from "@mui/material/TextField";
import { useLazyQuery } from "@apollo/client";
import { AsyncSearchInputProps, SelectOption } from "./types";
import {
  MIN_FILTER_LENGTH,
  DEBOUNCE_DELAY,
  DEFAULT_PLACEHOLDER,
  ERROR_MESSAGE,
  MIN_CHAR_MESSAGE,
  NO_RESULTS_MESSAGE,
  LOADING_MESSAGE,
  DEFAULT_QUERY_ARGS,
} from "./constants";
import { getPlaceholder, getNestedData } from "./utils";
import { useDebounce } from "../../../hooks/useDebounce";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import { TAXONOMY_CLIENT } from "../../../utils/consts";

export function AsyncSearchInput<T extends SelectOption, D = unknown>({
  query,
  id = "async-search-input",
  searchKey,
  selectedOptions = [],
  onOptionsSelect,
  convertResponseToOptions,
  responseDataPath,
  clientName = TAXONOMY_CLIENT,
  placeholderText = DEFAULT_PLACEHOLDER,
  minFilterLength = MIN_FILTER_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  errorMessage = ERROR_MESSAGE,
  minCharMessage = MIN_CHAR_MESSAGE,
  noResultsMessage = NO_RESULTS_MESSAGE,
  loadingMessage = LOADING_MESSAGE,
  queryArgs = DEFAULT_QUERY_ARGS,
  showGlobalLoadingIndicator = false,
  maxVisibleChips = 10,
  showDoneButton = false,
  autoCompleteProps,
}: AsyncSearchInputProps<T, D>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<T[]>(selectedOptions);
  const [searchProcessed, setSearchProcessed] = useState(false);
  const [open, setOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

  // Use ref to avoid re-renders when creating stable callbacks
  const stateRef = useRef({ searchTerm, selectedOptions });
  useEffect(() => {
    stateRef.current = { searchTerm, selectedOptions };
  }, [searchTerm, selectedOptions]);

  const resetSearch = useCallback(() => {
    setSearchResults([]);
    setSearchProcessed(false);
  }, []);

  const handleQueryCompleted = useCallback(
    (data: D) => {
      const rawData = getNestedData(data, responseDataPath);
      const options = convertResponseToOptions(rawData);
      setSearchResults(options);
      setSearchProcessed(true);
    },
    [responseDataPath, convertResponseToOptions]
  );

  const apolloContext = useMemo(() => ({ clientName }), [clientName]);

  const [getData, { loading, error }] = useLazyQuery(query, {
    context: apolloContext,
    onCompleted: handleQueryCompleted,
  });

  const handleOpen = useCallback((_: React.SyntheticEvent, isOpen: boolean) => {
    setOpen(isOpen);
  }, []);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input" && newInputValue.length === 0) {
        setOpen(false);
        resetSearch();
      }

      setSearchTerm(newInputValue);
    },
    [resetSearch]
  );

  const placeholder = useMemo(
    () => getPlaceholder(searchResults.length, placeholderText),
    [searchResults.length, placeholderText]
  );

  const handleOptionsChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T[], reason: string) => {
      if (reason === "clear") resetSearch();
      onOptionsSelect(newValue);
    },
    [onOptionsSelect, resetSearch]
  );

  const noOptionsText = useMemo(() => {
    if (error) return errorMessage;
    if (searchTerm.length < minFilterLength) return minCharMessage;
    if (searchResults.length === 0 && searchProcessed) return noResultsMessage;
    return loadingMessage;
  }, [
    searchTerm,
    searchResults.length,
    searchProcessed,
    error,
    errorMessage,
    minFilterLength,
    minCharMessage,
    noResultsMessage,
    loadingMessage,
  ]);

  // Reset search when no options are selected and no search term is entered
  useEffect(() => {
    if (selectedOptions.length === 0 && debouncedSearchTerm.length === 0) {
      resetSearch();
    }
  }, [selectedOptions.length, debouncedSearchTerm.length, resetSearch]);

  // Fetch search results when search term is longer than minFilterLength
  useEffect(() => {
    if (debouncedSearchTerm.length >= minFilterLength) {
      resetSearch();
      const variables = { [searchKey]: debouncedSearchTerm, ...queryArgs };
      getData({ variables });
    }
  }, [
    debouncedSearchTerm,
    getData,
    resetSearch,
    searchKey,
    minFilterLength,
    queryArgs,
  ]);

  const isLoadingState = useMemo(() => {
    return (
      loading ||
      (searchTerm.length >= minFilterLength &&
        searchTerm !== debouncedSearchTerm)
    );
  }, [loading, searchTerm, debouncedSearchTerm, minFilterLength]);

  // Stable callback for "+X more" chip click
  const handleMoreChipClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    const { selectedOptions: currentSelectedOptions } = stateRef.current;
    if (currentSelectedOptions.length > 0) {
      setSearchResults(currentSelectedOptions);
    }
    setOpen(true);
  }, []);

  // Custom renderTags function to show "+X more" chip
  const renderTags = useCallback(
    (
      tagValue: T[],
      getTagProps: (params: {
        index: number;
      }) => React.HTMLAttributes<HTMLElement>
    ) => {
      const numTags = tagValue.length;
      const tagsToShow =
        maxVisibleChips && numTags > maxVisibleChips
          ? maxVisibleChips
          : numTags;

      return (
        <>
          {tagValue.slice(0, tagsToShow).map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                id={`${id}-chip-${option.value}`}
                key={index}
                label={option.label}
                size="small"
                onDelete={(tagProps as { onDelete?: () => void }).onDelete}
              />
            );
          })}
          {maxVisibleChips && numTags > maxVisibleChips && (
            <Chip
              id={`${id}-more-chip`}
              key="more"
              label={`+${numTags - maxVisibleChips} more`}
              size="small"
              onClick={handleMoreChipClick}
              sx={{
                bgcolor: "primary.light",
                color: "primary.contrastText",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "primary.main",
                },
              }}
            />
          )}
        </>
      );
    },
    [maxVisibleChips, handleMoreChipClick, id]
  );

  const DonePaperComponent = useCallback(
    (paperProps: React.HTMLAttributes<HTMLElement>) => (
      <Paper {...paperProps}>
        {paperProps.children}
        <Divider />
        <Button
          fullWidth
          onClick={() => setOpen(false)}
          data-testid="scoring-products-done-btn"
          sx={{ py: 1, fontWeight: 700 }}
        >
          DONE
        </Button>
      </Paper>
    ),
    []
  );

  return (
    <Box id={`${id}-container`} position="relative" width="100%">
      <Autocomplete
        {...autoCompleteProps}
        {...(showDoneButton ? { PaperComponent: DonePaperComponent } : {})}
        id={`${id}-autocomplete`}
        size="small"
        disablePortal={autoCompleteProps?.disablePortal ?? true}
        multiple
        disableCloseOnSelect
        autoSelect={false}
        clearOnBlur={false}
        selectOnFocus={false}
        limitTags={maxVisibleChips}
        renderValue={renderTags}
        value={selectedOptions}
        options={searchResults}
        loading={isLoadingState}
        onInputChange={handleInputChange}
        onChange={handleOptionsChange}
        open={open}
        onOpen={(e: React.SyntheticEvent) => handleOpen(e, true)}
        onClose={(e: React.SyntheticEvent) => handleOpen(e, false)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={(x) => x}
        slotProps={{
          listbox: {
            id: `${id}-listbox`,
            sx: {
              maxHeight: 200,
              ...lightScrollbarStyle,
            },
          },
          popper: {
            id: `${id}-popper`,
            modifiers: [
              { name: "flip", enabled: true },
              {
                name: "preventOverflow",
                enabled: true,
                options: { padding: 8 },
              },
            ],
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={`${id}-input`}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                gap: 0.5,
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps}>
              <Checkbox
                checked={selected}
                sx={{ marginRight: 1, padding: 0 }}
                disableRipple
              />
              {option.label}
            </li>
          );
        }}
        noOptionsText={noOptionsText}
      />
      {showGlobalLoadingIndicator && (
        <>
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(1px)",
              borderRadius: 1,
              zIndex: 10,
            }}
          />
          <Stack
            alignItems="center"
            justifyContent="center"
            position="absolute"
            top="50%"
            right="10px"
            sx={{
              transform: "translateY(-50%)",
              zIndex: 11,
            }}
          >
            <CircularProgress size={20} />
          </Stack>
        </>
      )}
    </Box>
  );
}
